import { store } from '@redux/store';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import { userProfileActions } from 'features/userProfile';
import { selectModal } from 'layout/redux/selectors';
import { closeButton, privacyPolicyParagraph } from '../constants/sharedHtml';
import leadConstructor from '../services/leadConstructor';
import { postLeadAskQuestionForm } from '../services/taskFlow';
import AskQuestionButton from './askQuestionButton';
import CommentsTextArea from './commentsTextArea';
import EmailInput from './emailInput';
import NameInput from './nameInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';
import RealtorDropdown from './realtorDropdown';
import LegalText from './legalText';

/* Creates a new instance of a Ask Question Form to allow users asking question about the selected housing unit. */
export default class AskQuestionFormPaid {
	constructor() {
		const state = store.getState();
		const userProfile = state?.userProfile || {};
		const { id, guid, type, fromPage} = selectModal(state);
		this.id = id;
		this.guid = guid;
		this.type = type;
		this.fromPage = fromPage;
		this.excludeRecommendations = false;
		this.getAskQuestionButton = new AskQuestionButton();
		this.commentsTextArea = new CommentsTextArea();
		this.emailAddressInput = new EmailInput();
		this.emailAddressInput.value = userProfile.email;
		this.formId = 'askQuestionFormPaid';
		this.fullNameInput = new NameInput();
		this.fullNameInput.value = userProfile.name;
		this.phoneNumberInput = new PhoneInput();
		this.phoneNumberInput.value = userProfile.phone;
		this.postalCodeInput = new ZipInput();
		this.postalCodeInput.value = userProfile.zip;
		this.postalCodeInput.isInternational = userProfile.livesOutside;
		this.realtorDropdown = new RealtorDropdown();
		this.realtorDropdown.value = userProfile.realtorQuestion;
		this.onClickEvent = null;
		this.legalText = new LegalText(privacyPolicyParagraph);
	}

	/**
		* validateNode: Check if the field has a value as expected.
		* @param {Object} node Input to validate
		* @returns A boolean value indicating whether the input is valid or not.
		*/
	static validateNode(node) {
		return node.validate();
	}

	/**
		* getFormOpenedAnalyticsData: Gets the object required for the Google Analytics events on form opened.
		* @param {Object} properties Information regarding to the listing.
		* @returns The Google Analtic event information.
		*/
	static getFormOpenedAnalyticsData(properties) {
		const customDimensions = {};

		if (properties.marketId) {
			customDimensions[[GoogleAnalyticsCustomDimensions.MarketId().getName()]] = properties.marketId;
		}

		if (properties.marketName && properties.stateAbbreviation) {
			customDimensions[
				[GoogleAnalyticsCustomDimensions.MarketName().getName()]
			] = `${properties.marketName}, ${properties.stateAbbreviation}`;
		}

		if (properties.city) {
			customDimensions[[GoogleAnalyticsCustomDimensions.City().getName()]] = properties.city;
		}

		return new GoogleAnalyticsEvent(
			'Lead Events',
			'Search Results CTA Ask Question',
			'Open Form - Ask Question',
			customDimensions
		);
	}

	/**
		* setHousingData: Sets the housing information for the form.
		* @param {Object} properties Information regarding to the listing.
		*/
	setHousingData(properties = {}) {
		this.housingData = properties;
	}

	/**
	* submitAskQuestionForm: Process to validate and submit the form.
	* @param {event} event Click event.
	*/
	async submitAskQuestionForm(event) {
		event.preventDefault();

		const validationFunctions = [
		  this.emailAddressInput,
		  this.fullNameInput,
		  this.postalCodeInput,
		  this.phoneNumberInput,
		];

		this.getAskQuestionButton.getNode().disabled = 'disabled';

		const results = await Promise.all(
		  validationFunctions.map((input) => AskQuestionFormPaid.validateNode(input))
		);

		if (results.some((isValid) => !isValid)) {
		  this.getAskQuestionButton.getNode().disabled = false;
		  return;
		}

		const formData = new FormData(this.getForm());
		const formInformation = Object.fromEntries(formData.entries());

		formInformation.id = this.id;
		formInformation.type = `${this.type}-brochure`;
		formInformation.excludeRecommendations = this.excludeRecommendations;
		formInformation.fromPage = this.housingData.fromPage;
		formInformation.guid = this.guid;

		const leadObject = leadConstructor(formInformation);
		leadObject.setHousingInformation?.(this.housingData.listingInformation);

		postLeadAskQuestionForm(leadObject);
	}

   /**
   * handleEmailValidationResult: Handles the result of the email validation.
   * @param {Object} isValid The result of the email validation.
   */
	handleEmailValidationResult(isValid) {
	this.getAskQuestionButton.getNode().disabled = false;
		if (isValid) {
			this.emailAddressInput.setValidState(this.emailAddressInput.getNode());
			this.emailAddressInput.toggleEmailValidationDataAttribute(true);
		} else {
			this.emailAddressInput.setInvalidState(this.emailAddressInput.getNode());
			this.emailAddressInput.toggleEmailValidationDataAttribute(false);
		}
	}

	/**
	* Sets the binding required for the form.
	*/
	bindEvents() {
		this.onClickEvent = this.submitAskQuestionForm.bind(this);
		this.phoneNumberInput.bindEvents();
		this.postalCodeInput.bindEvents();
		this.legalText.bindEvents();
		if (this.fullNameInput.value === '') {
			this.fullNameInput.focus();
		}
    this.realtorDropdown.setSelectedValue();
		this.getAskQuestionButton.getNode().addEventListener('click', this.onClickEvent);
		this.bindChangeEvents();
	}

	/**
	* bindChangeEvents: Get the form inputs then subscribe each OnChangeEvent to store and persist their values
	*/
	bindChangeEvents() {
		const changeInputs = [
			this.emailAddressInput,
			this.fullNameInput,
			this.postalCodeInput,
			this.phoneNumberInput,
      		this.realtorDropdown,
		];

		changeInputs.forEach(input => input.getOnChangeEvent().subscribe(data =>
			store.dispatch(userProfileActions.update(data))));
	}

	/**
		* Removes the bindings that should not be still using.
		*/
	unbindEvents() {
		this.getAskQuestionButton.getNode().removeEventListener('click', this.onClickEvent);
		this.legalText.unbindEvents();
	}

	/**
		* getForm: Gets the form container
		* @returns A HTML element related to the form container.
		*/
	getForm() {
		return document.getElementById(this.formId);
	}

	/**
		* Gets the HTML structure for the ask question form paid.
		* @returns The string with the HTML structure.
		*/
	getHtml() {
		this.legalText.setReplacements([
			{ key: "{0}", value: "Ask Question"},
			{ key: "{1}", value: this.housingData.listingInformation?.brandName ?? "the builder" },
		]);
    return `
    <div class="lead-form form--brochure">
      ${closeButton}
      <p class="form__title">Ask a question</p>
      <form id='${this.formId}'>
        <fieldset>
          <div class="form__field">
            ${this.fullNameInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.emailAddressInput.getHtml()}
          </div>
          <div class="form__field">
            ${this.phoneNumberInput.getHtml()}
          </div>
          <div class="form__field field--split">
            ${this.postalCodeInput.getHtml()}
          </div>
          <div class="form__field agent--field">
            ${this.realtorDropdown.getHtml()}
          </div>
          <div class="form__field">
            ${this.commentsTextArea.getHtml()}
          </div>
          <div class="form__button-bar">
            ${this.getAskQuestionButton.getHtml()}
          </div>
          <input type="hidden" id="leadAction" name="leadAction" value="AskQuestion" />
        </fieldset>        
      </form>
      ${this.legalText.getHtml()}
    </div>`;
  }
}
